<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { fetchJobs, claimJob, type Job } from '../game/quests-api';
import { toast } from '../game/toast';
import { BG, COMBAT_SCENE, tierBadge } from '../game/art';
import cashIcon from '../assets/omerta/icons/cash.webp';
import goldBarsIcon from '../assets/omerta/icons/gold-bars.webp';
import nerveIcon from '../assets/omerta/icons/nerve.webp';

const emit = defineEmits<{ (e: 'updated'): void }>();

const loading = ref(true);
const error = ref('');
const busy = ref<string | null>(null);
const collectingAll = ref(false);
const jobs = ref<Job[]>([]);
/** Job id currently playing its "COLLECTED" stamp moment. */
const stamped = ref<string | null>(null);

const daily = computed(() => jobs.value.filter((j) => j.type === 'DAILY'));
const weekly = computed(() => jobs.value.filter((j) => j.type === 'WEEKLY'));
const claimables = computed(() => jobs.value.filter((j) => j.claimable));
const doneToday = computed(() => daily.value.filter((j) => j.claimable || j.status === 'COMPLETED').length);

/** Summed payout waiting across every claimable job (for the Collect-all pitch). */
const waiting = computed(() => claimables.value.reduce(
  (acc, j) => ({
    cash: acc.cash + j.reward.cash,
    bars: acc.bars + j.reward.goldBars,
    xp: acc.xp + j.reward.xp,
  }),
  { cash: 0, bars: 0, xp: 0 },
));

// ── Reset clocks — mirrors the backend cron truth (core/cron/cron.service.ts):
//    dailies reset 00:00 UTC, weeklies Monday 00:00 UTC. Display only.
const now = ref(Date.now());
let tick: number | undefined;
onMounted(() => { tick = window.setInterval(() => { now.value = Date.now(); }, 1000); });
onUnmounted(() => { if (tick) window.clearInterval(tick); });

function nextUtcMidnight(): number {
  const d = new Date(now.value);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1);
}
function nextUtcMonday(): number {
  const d = new Date(now.value);
  const days = (8 - d.getUTCDay()) % 7 || 7; // days until next Monday
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days);
}
function clock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const hh = String(h).padStart(2, '0'), mm = String(m).padStart(2, '0'), ss = String(sec).padStart(2, '0');
  return d > 0 ? `${d}d ${hh}:${mm}:${ss}` : `${hh}:${mm}:${ss}`;
}
const dailyClock = computed(() => clock(nextUtcMidnight() - now.value));
const weeklyClock = computed(() => clock(nextUtcMonday() - now.value));

// ── Per-action dossier art: a small scene/icon medallion + an accent color.
// EVERY seeded action (jobs.constants.ts JOB_ACTIONS) must resolve to an image —
// an unmapped action used to render as an empty circle (QA: "Blood in the Pit"
// had no entry here at all, since 'pvp_win' was missing from this table).
const ACTION_LOOK: Record<string, { img?: string; accent: string }> = {
  enemies_beaten: { img: COMBAT_SCENE.strike, accent: '#c0504d' },
  cash_earned:    { img: cashIcon,            accent: '#c8a24c' },
  tiles_moved:    { img: COMBAT_SCENE.flee,   accent: '#5b9bd5' },
  pvp_win:        { img: nerveIcon,           accent: '#8f2323' }, // brass knuckles — the Pit
  explored:       { img: COMBAT_SCENE.guard,  accent: '#5fc2ad' },
  market_bought:  { img: BG.market,           accent: '#c96a9e' },
  chat_sent:      { img: BG.office,           accent: '#9a86ab' },
  turf_taken:     { img: tierBadge(),         accent: '#d98e32' },
};
// Fallback for any job action NOT in the table above (a future job def added
// without updating this file) — a generic tier badge beats a blank circle.
const DEFAULT_LOOK = { img: tierBadge(), accent: 'var(--color-gold)' };
const look = (j: Job) => ACTION_LOOK[j.action] ?? DEFAULT_LOOK;

function pct(j: Job): number {
  if (j.target <= 0) return 0;
  return Math.min(100, Math.round((Math.min(j.progress, j.target) / j.target) * 100));
}
/** Small targets read better as pips; big tallies as a filled track. */
const usePips = (j: Job) => j.target <= 12;

async function refresh() {
  error.value = '';
  try { jobs.value = (await fetchJobs()).jobs; }
  catch (e) { error.value = (e as Error).message; }
}
onMounted(async () => { await refresh(); loading.value = false; });

async function onClaim(j: Job) {
  if (!j.claimable || busy.value) return;
  busy.value = j.id;
  error.value = '';
  try {
    const res = await claimJob(j.id);
    const parts: string[] = [];
    if (res.reward.soft) parts.push(`+${res.reward.soft.toLocaleString()} cash`);
    if (res.reward.premium) parts.push(`+${res.reward.premium.toLocaleString()} gold bars`);
    if (res.reward.xp) parts.push(`+${res.reward.xp.toLocaleString()} XP`);
    toast(`Payday — ${j.name}: ${parts.join(' · ')}`, { icon: cashIcon });
    stamped.value = j.id;                         // let the COLLECTED stamp land…
    await new Promise((r) => setTimeout(r, 650)); // …before the card refreshes
    await refresh();
    emit('updated');
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    busy.value = null;
    stamped.value = null;
  }
}

async function onCollectAll() {
  if (collectingAll.value || claimables.value.length === 0) return;
  collectingAll.value = true;
  error.value = '';
  try {
    const total = { soft: 0, premium: 0, xp: 0 };
    for (const j of claimables.value) {
      try {
        const res = await claimJob(j.id);
        total.soft += res.reward.soft; total.premium += res.reward.premium; total.xp += res.reward.xp;
      } catch { /* keep collecting the rest */ }
    }
    const parts: string[] = [];
    if (total.soft) parts.push(`+${total.soft.toLocaleString()} cash`);
    if (total.premium) parts.push(`+${total.premium.toLocaleString()} gold bars`);
    if (total.xp) parts.push(`+${total.xp.toLocaleString()} XP`);
    if (parts.length) toast(`All paydays collected: ${parts.join(' · ')}`, { icon: cashIcon });
    await refresh();
    emit('updated');
  } finally {
    collectingAll.value = false;
  }
}
</script>

<template>
  <section>
    <!-- banner -->
    <div class="corner-frame relative rounded-lg overflow-hidden mb-5 h-36">
      <img :src="BG.jobs" alt="" class="w-full h-full object-cover" />
      <div class="absolute inset-0" style="background:linear-gradient(180deg,rgba(10,7,20,0.25),rgba(10,7,20,0.94))"></div>
      <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
      <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
      <div class="absolute bottom-3 left-4">
        <h1 class="font-heading text-2xl text-gold">Jobs</h1>
        <span class="font-label uppercase tracking-wider text-sm text-text-dim block">Work for the family — collect your end</span>
      </div>
    </div>

    <!-- the ledger strip -->
    <div class="flex flex-wrap items-center gap-3 mb-6">
      <span class="jb-chip">Day book <b class="text-text ml-1">{{ doneToday }}/{{ daily.length }}</b></span>
      <span class="jb-chip">New slate in <b class="text-gold ml-1 tabular-nums">{{ dailyClock }}</b></span>
      <span class="jb-chip">Big scores turn Monday <b class="text-gold ml-1 tabular-nums">{{ weeklyClock }}</b></span>
      <div class="flex-1"></div>
      <button
        v-if="claimables.length >= 2"
        class="btn-deco btn-deco--gold btn-deco--sm whitespace-normal text-center leading-snug"
        :disabled="collectingAll"
        @click="onCollectAll"
      >
        {{ collectingAll ? 'Collecting…' : `Collect all — ${claimables.length} payoffs waiting` }}
        <span v-if="waiting.cash" class="ml-1">· {{ waiting.cash.toLocaleString() }} cash</span>
        <span v-if="waiting.bars" class="ml-1">· {{ waiting.bars }} bars</span>
      </button>
    </div>

    <p v-if="loading" class="text-text-dim">Opening the books…</p>
    <p v-if="error" class="mb-4 text-sm text-error">{{ error }}</p>

    <div v-if="!loading" class="grid gap-9">
      <div
        v-for="grp in [
          { key: 'daily', title: 'Daily jobs', blurb: 'Quick work. The slate wipes at midnight — unclaimed pay wipes with it.', list: daily },
          { key: 'weekly', title: 'The big scores', blurb: 'Serious contracts for serious earners. A full week to deliver.', list: weekly },
        ]"
        :key="grp.key"
      >
        <div class="flex items-baseline gap-3 mb-1">
          <h2 class="font-heading text-xl text-gold">{{ grp.title }}</h2>
          <span class="h-px flex-1 self-center" style="background:linear-gradient(90deg,var(--color-border),transparent)"></span>
        </div>
        <p class="font-body italic text-xs text-text-dim mb-4">{{ grp.blurb }}</p>

        <p v-if="grp.list.length === 0" class="font-body italic text-text-dim text-sm">
          Nothing on the books. The city is quiet — too quiet.
        </p>

        <div v-else class="grid sm:grid-cols-2 gap-4">
          <article
            v-for="j in grp.list" :key="j.id"
            class="jb-card corner-frame relative"
            :class="{ 'jb-ready': j.claimable, 'jb-stamped': stamped === j.id }"
            :style="{ '--jb-accent': look(j).accent }"
          >
            <template v-if="j.claimable">
              <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
              <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
            </template>

            <!-- PAYDAY / COLLECTED stamps -->
            <span v-if="j.claimable && stamped !== j.id" class="jb-stamp">Payday</span>
            <span v-if="stamped === j.id" class="jb-stamp jb-stamp-paid">Collected</span>

            <div class="flex items-center gap-3.5">
              <!-- dossier medallion -->
              <div class="jb-medal shrink-0">
                <img v-if="look(j).img" :src="look(j).img" alt="" class="w-full h-full object-cover" />
              </div>

              <div class="min-w-0 flex-1">
                <h3 class="font-heading text-[17px] leading-tight text-text">{{ j.name }}</h3>
                <p class="font-body italic text-xs text-text-dim mt-0.5 leading-snug">{{ j.description }}</p>

                <!-- progress: pips for short jobs, a track for tallies -->
                <div class="mt-3">
                  <div v-if="usePips(j)" class="flex items-center gap-[5px]">
                    <i v-for="n in j.target" :key="n" class="jb-pip" :class="{ 'jb-pip-lit': n <= Math.min(j.progress, j.target) }"></i>
                    <span class="font-label text-[10px] tracking-wider text-text-dim ml-2 tabular-nums">{{ Math.min(j.progress, j.target) }}/{{ j.target }}</span>
                  </div>
                  <div v-else>
                    <div class="jb-track">
                      <div class="jb-fill" :class="{ 'jb-fill-hot': pct(j) >= 80 && !j.claimable }" :style="{ width: pct(j) + '%' }"></div>
                    </div>
                    <div class="font-label text-[10px] tracking-wider text-text-dim mt-1 tabular-nums">
                      {{ Math.min(j.progress, j.target).toLocaleString() }} / {{ j.target.toLocaleString() }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- the end: reward chips + claim -->
            <div class="flex items-center gap-2 mt-3.5 pt-3 border-t border-border/60">
              <span v-if="j.reward.cash" class="jb-reward"><img :src="cashIcon" alt="cash" class="w-4 h-4 object-contain shrink-0" />{{ j.reward.cash.toLocaleString() }}</span>
              <span v-if="j.reward.goldBars" class="jb-reward"><img :src="goldBarsIcon" alt="gold bars" class="w-4 h-4 object-contain shrink-0" /><b class="text-gold-bright">{{ j.reward.goldBars }}</b></span>
              <span v-if="j.reward.xp" class="jb-reward jb-reward-xp">&#9670; {{ j.reward.xp }} XP</span>
              <div class="flex-1"></div>
              <button
                v-if="j.claimable"
                :disabled="busy === j.id"
                @click="onClaim(j)"
                class="btn-deco btn-deco--gold btn-deco--sm"
              >{{ busy === j.id ? '…' : 'Collect' }}</button>
              <span
                v-else
                class="jb-status"
                title="Still working this one — progress ticks as you play the city. Come back once it's done to collect."
              ><i class="jb-status-dot" aria-hidden="true"></i>On the job</span>
            </div>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.jb-chip {
  display: inline-flex; align-items: baseline;
  padding: 5px 12px;
  font-family: var(--font-label, sans-serif); font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--color-text-dim);
  border: 1px solid var(--color-border); border-radius: 999px;
  background: rgba(255,255,255,0.02);
}

.jb-card {
  padding: 1rem 1.05rem 0.85rem;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background:
    radial-gradient(130% 80% at 0% 0%, color-mix(in srgb, var(--jb-accent, #c8a24c) 7%, transparent), transparent 55%),
    linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.14)),
    var(--color-surface);
  transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;
}
.jb-card:hover { transform: translateY(-2px); box-shadow: 0 8px 22px rgba(0,0,0,0.45); }

.jb-ready {
  border-color: color-mix(in srgb, var(--color-gold) 60%, transparent);
  animation: jb-ready-glow 2.6s ease-in-out infinite;
}
@keyframes jb-ready-glow {
  0%, 100% { box-shadow: 0 0 10px rgba(200,162,76,0.12); }
  50% { box-shadow: 0 0 22px rgba(227,200,120,0.3); }
}

.jb-medal {
  width: 52px; height: 52px;
  border-radius: 50%; overflow: hidden;
  border: 2px solid color-mix(in srgb, var(--jb-accent, #c8a24c) 65%, transparent);
  box-shadow: 0 3px 10px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(0,0,0,0.4);
  background: var(--color-surface-2);
}

.jb-pip {
  width: 9px; height: 9px; transform: rotate(45deg); border-radius: 1.5px;
  background: rgba(255,255,255,0.06); border: 1px solid rgba(200,162,76,0.35);
  transition: background 220ms ease, box-shadow 220ms ease;
}
.jb-pip-lit {
  background: linear-gradient(135deg, var(--color-gold-bright), var(--color-gold));
  box-shadow: 0 0 6px rgba(227,200,120,0.5);
}

.jb-track {
  height: 9px; border-radius: 5px; overflow: hidden;
  background: var(--color-bg); border: 1px solid var(--color-border);
}
.jb-fill {
  height: 100%; position: relative;
  background: linear-gradient(90deg, var(--color-gold), var(--color-gold-bright));
  transition: width 600ms cubic-bezier(0.3, 0.8, 0.3, 1);
}
.jb-fill-hot::after {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(115deg, transparent 30%, rgba(255,244,214,0.55) 50%, transparent 70%);
  animation: shimmer-sweep 1.8s ease-in-out infinite;
}

.jb-stamp {
  position: absolute; top: 10px; right: 12px; z-index: 2;
  transform: rotate(9deg);
  font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase;
  color: rgba(140, 26, 20, 0.9);
  border: 2px solid rgba(140, 26, 20, 0.75); border-radius: 3px;
  padding: 3px 9px;
  box-shadow: inset 0 0 0 1px rgba(140, 26, 20, 0.35);
  background: rgba(20, 8, 6, 0.25);
  pointer-events: none;
}
.jb-stamp-paid {
  color: rgba(111, 158, 84, 0.95);
  border-color: rgba(111, 158, 84, 0.8);
  box-shadow: inset 0 0 0 1px rgba(111, 158, 84, 0.4);
  background: rgba(8, 18, 8, 0.3);
  animation: jb-slam 300ms cubic-bezier(0.2, 1.6, 0.4, 1) both;
}
@keyframes jb-slam {
  0% { transform: rotate(9deg) scale(2.4); opacity: 0; }
  100% { transform: rotate(-6deg) scale(1); opacity: 1; }
}
.jb-stamped { border-color: rgba(111, 158, 84, 0.5); }

.jb-reward {
  display: inline-flex; align-items: center; gap: 5px;
  font-family: var(--font-heading, serif); font-size: 13px; color: var(--color-gold);
  padding: 2px 9px;
  border: 1px solid color-mix(in srgb, var(--color-gold) 25%, transparent);
  border-radius: 999px;
  background: rgba(200, 162, 76, 0.06);
  font-variant-numeric: tabular-nums;
}
.jb-reward-xp { color: var(--color-purple-light); border-color: color-mix(in srgb, var(--color-purple-light) 30%, transparent); background: rgba(154, 134, 171, 0.07); }

/* ── jb-status — a passive STATUS reading, not a button. Claimable jobs still
   get the gold "Collect" plaque; everything still in progress gets this quiet
   letterspaced chip with a pulsing dot instead — no border-plaque affordance
   that invites a click, since there's nothing to click yet. */
.jb-status {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 6px 2px;
  font-family: var(--font-label, sans-serif); font-size: 10px; font-weight: 600;
  letter-spacing: 0.18em; text-transform: uppercase;
  color: var(--color-text-dim);
  cursor: default; user-select: none;
}
.jb-status-dot {
  width: 6px; height: 6px; border-radius: 50%; flex: none;
  background: color-mix(in srgb, var(--jb-accent, var(--color-gold)) 70%, var(--color-text-dim));
  animation: jb-status-pulse 1.8s ease-in-out infinite;
}
@keyframes jb-status-pulse {
  0%, 100% { opacity: 0.4; box-shadow: 0 0 0 0 color-mix(in srgb, var(--jb-accent, var(--color-gold)) 40%, transparent); }
  50% { opacity: 1; box-shadow: 0 0 5px 1.5px color-mix(in srgb, var(--jb-accent, var(--color-gold)) 45%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .jb-ready, .jb-fill-hot::after { animation: none; }
  .jb-stamp-paid { animation: none; }
  .jb-status-dot { animation: none; opacity: 0.85; }
  .jb-card, .jb-card:hover { transition: none; transform: none; }
}
</style>
